import React, { useState, useEffect } from 'react'
import { CreditCard, Plus, Trash2, Loader2 } from 'lucide-react'
import { Button } from '../ui'
import { useToast } from '../../contexts/ToastContext'
import {
  paymentMethodService,
  PaymentMethod
} from '../../services/paymentMethodService'

interface PaymentMethodsSectionProps {
  onAddPaymentMethod: () => void
}

const PaymentMethodsSection: React.FC<PaymentMethodsSectionProps> = ({
  onAddPaymentMethod
}) => {
  const { showError, showSuccess } = useToast()
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([])
  const [loading, setLoading] = useState(true)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [settingDefaultId, setSettingDefaultId] = useState<string | null>(null)

  const fetchPaymentMethods = async () => {
    try {
      setLoading(true)
      const response = await paymentMethodService.getPaymentMethods()

      if (response.success) {
        setPaymentMethods(response.data || [])
      } else {
        showError(
          'Failed to load payment methods',
          response.message || 'Please try again'
        )
      }
    } catch (error) {
      console.error('Error fetching payment methods:', error)
      showError('Error', 'Failed to load payment methods')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchPaymentMethods()
  }, [])

  const handleSetDefault = async (stripePaymentMethodId: string) => {
    try {
      setSettingDefaultId(stripePaymentMethodId)
      const response = await paymentMethodService.setDefaultPaymentMethod(
        stripePaymentMethodId
      )

      if (response.success) {
        showSuccess('Default Updated', 'Payment method set as default')
        await fetchPaymentMethods()
      } else {
        showError(
          'Update Failed',
          response.message || 'Failed to set default payment method'
        )
      }
    } catch (error) {
      console.error('Error setting default:', error)
      showError('Error', 'Failed to set default payment method')
    } finally {
      setSettingDefaultId(null)
    }
  }

  const handleDelete = async (stripePaymentMethodId: string) => {
    try {
      setDeletingId(stripePaymentMethodId)
      const response = await paymentMethodService.deletePaymentMethod(
        stripePaymentMethodId
      )

      if (response.success) {
        showSuccess('Removed', 'Payment method has been removed')
        await fetchPaymentMethods()
      } else {
        showError(
          'Delete Failed',
          response.message || 'Failed to remove payment method'
        )
      }
    } catch (error) {
      console.error('Error deleting payment method:', error)
      showError('Error', 'Failed to remove payment method')
    } finally {
      setDeletingId(null)
    }
  }

  const getCardBrandIcon = (brand: string | undefined) => {
    if (!brand) {
      return <CreditCard className='w-5 h-5 text-gray-500' />
    }

    const brandLower = brand.toLowerCase()

    switch (brandLower) {
      case 'visa':
        return (
          <div className='w-8 h-5 bg-gradient-to-r from-blue-600 to-blue-800 rounded text-white text-[8px] font-bold flex items-center justify-center'>
            VISA
          </div>
        )
      case 'mastercard':
        return (
          <div className='w-8 h-5 flex items-center justify-center'>
            <div className='w-3 h-3 rounded-full bg-red-500 -mr-1'></div>
            <div className='w-3 h-3 rounded-full bg-yellow-500'></div>
          </div>
        )
      case 'amex':
      case 'american_express':
        return (
          <div className='w-8 h-5 bg-blue-500 rounded text-white text-[6px] font-bold flex items-center justify-center'>
            AMEX
          </div>
        )
      default:
        return <CreditCard className='w-5 h-5 text-gray-500' />
    }
  }

  if (loading) {
    return (
      <div className='p-5 bg-gray-50 rounded-lg border border-gray-200'>
        <div className='flex items-center justify-center py-4'>
          <Loader2 className='w-5 h-5 animate-spin text-gray-500' />
          <span className='ml-2 text-sm text-gray-500'>
            Loading payment methods...
          </span>
        </div>
      </div>
    )
  }

  return (
    <div className='space-y-3'>
      {paymentMethods.length === 0 ? (
        <div className='p-5 bg-gray-50 rounded-lg border border-gray-200'>
          <div className='flex items-center gap-4'>
            <div className='p-2.5 bg-white rounded-lg border border-gray-200'>
              <CreditCard className='h-5 w-5 text-gray-400' />
            </div>
            <div className='flex-1'>
              <p className='text-sm font-medium text-gray-900'>
                No payment methods
              </p>
              <p className='text-xs text-gray-500 mt-0.5'>
                Add a payment method to manage your subscription
              </p>
            </div>
            <Button variant='primary' size='xs' onClick={onAddPaymentMethod}>
              <Plus className='h-4 w-4 mr-1.5' />
              Add Card
            </Button>
          </div>
        </div>
      ) : (
        <>
          {paymentMethods.map((method) => (
            <div
              key={method.id}
              className={`p-4 rounded-lg border transition-all ${
                method.is_default
                  ? 'bg-blue-50 border-blue-200'
                  : 'bg-gray-50 border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className='flex items-center justify-between'>
                <div className='flex items-center gap-3'>
                  {getCardBrandIcon(method.brand)}
                  <div>
                    <div className='flex items-center gap-2'>
                      <span className='text-sm font-medium text-gray-900 capitalize'>
                        {method.brand || 'Card'}
                      </span>
                      <span className='text-sm text-gray-600'>
                        •••• {method.last_4 || '****'}
                      </span>
                      {method.is_default && (
                        <span className='inline-flex items-center gap-1 px-1.5 py-0.5 bg-blue-100 text-blue-700 text-[10px] font-semibold rounded'>
                          DEFAULT
                        </span>
                      )}
                    </div>
                    <p className='text-xs text-gray-500 mt-0.5'>
                      Expires {String(method.expiry_month).padStart(2, '0')}/
                      {method.expiry_year}
                    </p>
                  </div>
                </div>

                <div className='flex items-center gap-2'>
                  {!method.is_default && (
                    <Button
                      variant='ghost'
                      size='xs'
                      onClick={() =>
                        handleSetDefault(method.stripe_payment_method_id)
                      }
                      disabled={
                        settingDefaultId === method.stripe_payment_method_id
                      }
                      className='text-xs'
                    >
                      {settingDefaultId === method.stripe_payment_method_id ? (
                        <Loader2 className='w-3 h-3 animate-spin' />
                      ) : (
                        'Set Default'
                      )}
                    </Button>
                  )}
                  <Button
                    variant='ghost'
                    size='xs'
                    onClick={() =>
                      handleDelete(method.stripe_payment_method_id)
                    }
                    disabled={
                      deletingId === method.stripe_payment_method_id ||
                      method.is_default
                    }
                    className={`${
                      method.is_default
                        ? 'text-gray-300 cursor-not-allowed'
                        : 'text-red-600 hover:text-red-700 hover:bg-red-50'
                    }`}
                    title={
                      method.is_default
                        ? 'Cannot delete default payment method'
                        : 'Remove'
                    }
                  >
                    {deletingId === method.stripe_payment_method_id ? (
                      <Loader2 className='w-4 h-4 animate-spin' />
                    ) : (
                      <Trash2 className='w-4 h-4' />
                    )}
                  </Button>
                </div>
              </div>
            </div>
          ))}

          <Button
            variant='outline'
            size='xs'
            onClick={onAddPaymentMethod}
            className='w-full mt-2'
          >
            <Plus className='h-4 w-4 mr-1.5' />
            Add Another Card
          </Button>
        </>
      )}
    </div>
  )
}

export default PaymentMethodsSection
